import { Button, Flex, IconButton, Menu, MenuButton, MenuItem, MenuList } from "@chakra-ui/react";
import { LocalDate, Month } from "@js-joda/core";
import { CustomHeaderParams } from "./DatePickerJsJodaAdapter";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";

function toYear(localDate: LocalDate) {
  return localDate.year();
}

function toMonthName(month: Month) {
  return {
    [Month.JANUARY.ordinal()]: "January",
    [Month.FEBRUARY.ordinal()]: "February",
    [Month.MARCH.ordinal()]: "March",
    [Month.APRIL.ordinal()]: "April",
    [Month.MAY.ordinal()]: "May",
    [Month.JUNE.ordinal()]: "June",
    [Month.JULY.ordinal()]: "July",
    [Month.AUGUST.ordinal()]: "August",
    [Month.SEPTEMBER.ordinal()]: "September",
    [Month.OCTOBER.ordinal()]: "October",
    [Month.NOVEMBER.ordinal()]: "November",
    [Month.DECEMBER.ordinal()]: "December",
  }[month.ordinal()];
}

function shouldDisplayNextMonth(customHeaderCount: number, totalMonthsShown: number | undefined) {
  return totalMonthsShown === undefined || totalMonthsShown - 1 === customHeaderCount;
}

interface Props extends CustomHeaderParams {
  totalMonthsShown?: number;
  pickerYears?: [number, number];
}

const DatePickerHeader = (props: Props) => {
  const canMoveToNextMonth = shouldDisplayNextMonth(
    props.customHeaderCount,
    props.totalMonthsShown,
  );

  const firstYear = props.pickerYears?.[0] ?? props.monthDate.year() - 1;
  const yearsMenuItems: number[] = [
    ...range(props.pickerYears !== undefined ? props.pickerYears[1] - props.pickerYears[0] : 4).map(
      (i) => firstYear + i,
    ),
  ];

  return (
    <Flex>
      {props.customHeaderCount === 0 && (
        <IconButton
          aria-label="Previous month"
          icon={<ChevronLeftIcon />}
          variant="text"
          onClick={props.decreaseMonth.bind(this)}
        />
      )}
      <Flex alignItems="center" flex={1} gap={2} justifyContent="center">
        <Menu isLazy>
          <MenuButton as={Button} variant="ghost">
            {toMonthName(props.monthDate.month())}
          </MenuButton>
          <MenuList minWidth="fit-content">
            {Month.values().map((month) => (
              <MenuItem
                key={month.ordinal()}
                fontSize="md"
                onClick={() => props.changeMonth(month)}
              >
                {toMonthName(month)}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
        <Menu isLazy>
          <MenuButton as={Button} variant="ghost">
            {toYear(props.monthDate)}
          </MenuButton>
          <MenuList maxHeight={"xs"} minWidth="fit-content" overflowY={"scroll"}>
            {yearsMenuItems.map((year) => (
              <MenuItem key={year} fontSize="md" onClick={() => props.changeYear(year)}>
                {year}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      </Flex>
      {canMoveToNextMonth && (
        <IconButton
          aria-label="Next month"
          icon={<ChevronRightIcon />}
          variant="text"
          onClick={props.increaseMonth.bind(this)}
        />
      )}
    </Flex>
  );
};

function range(num: number): number[] {
  if (num < 0) {
    throw new Error(`${num} should be bigger than or equal to 0`);
  }

  return Array(num)
    .fill(0)
    .map((_, i) => i);
}

export default DatePickerHeader;
